import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ReplyIcon from '@mui/icons-material/Reply';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import {
  getTaskComments,
  addTaskComment,
  updateTaskComment,
  deleteTaskComment,
} from '../../../../../services/taskService';

const TaskComments = ({ taskId, onCommentAdded }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editText, setEditText] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  const [replyingToCommentId, setReplyingToCommentId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const accountData = useSelector((state) => state?.login?.user || null);
  const commentInputRef = useRef(null);
  const commentsContainerRef = useRef(null);

  const scrollToBottom = () => {
    if (commentsContainerRef.current) {
      commentsContainerRef.current.scrollTop = commentsContainerRef.current.scrollHeight;
    }
  };

  const handleScroll = () => {
    if (commentsContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = commentsContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollToBottom(!isNearBottom);
    }
  };

  const fetchComments = async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const response = await getTaskComments(taskId);
      const fetchedComments = response?.data?.data || [];
      setComments(fetchedComments);

      // Scroll to bottom after fetching to show latest comments
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } catch (error) {
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (taskId) {
      fetchComments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  useEffect(() => {
    const container = commentsContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      // Initial check
      handleScroll();
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
    return undefined;
  }, [comments]);

  const handleAddComment = async (parentCommentId = null) => {
    const commentText = parentCommentId ? replyText.trim() : newComment.trim();
    if (!commentText) {
      toast.error('Please enter a comment');
      return;
    }

    if (!taskId) {
      toast.error('Task ID is missing');
      return;
    }

    if (submitting) {
      return; // Prevent multiple submissions
    }

    try {
      setSubmitting(true);
      const response = await addTaskComment(taskId, commentText, parentCommentId);

      if (!response || !response.data) {
        toast.error('Invalid response from server');
        return;
      }

      const newCommentData = response.data.data || response.data;

      if (parentCommentId) {
        // Update the parent comment with the new reply
        // Ensure the reply has parentCommentId set
        const replyWithParent = {
          ...newCommentData,
          parentCommentId,
        };
        setComments((prev) =>
          prev.map((comment) => {
            if (comment.id === parentCommentId) {
              return {
                ...comment,
                replies: [...(comment.replies || []), replyWithParent],
              };
            }
            return comment;
          })
        );
        setReplyText('');
        setReplyingToCommentId(null);
      } else {
        // Add the new comment to the list
        setComments((prev) => [...prev, newCommentData]);
        setNewComment('');
      }

      if (onCommentAdded) onCommentAdded();
      toast.success('Comment added successfully');
      // Scroll to bottom to show the new comment
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } catch (error) {
      console.error('Error adding comment:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to add comment';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditText(comment.comment);
    setMenuAnchor(null);
  };

  const handleUpdateComment = async () => {
    if (!editText.trim()) return;

    try {
      const response = await updateTaskComment(editingCommentId, editText.trim());
      const updatedComment = response?.data?.data;

      // Update comment in the list, checking both top-level and nested replies
      setComments((prev) =>
        prev.map((c) => {
          // If this is the top-level comment being edited
          if (c.id === editingCommentId) {
            // Preserve replies if they exist
            return {
              ...updatedComment,
              replies: updatedComment.replies || c.replies || [],
            };
          }
          // Check if it's a reply that needs updating within this comment's replies
          if (c.replies && c.replies.some((r) => r.id === editingCommentId)) {
            return {
              ...c,
              replies: c.replies.map((r) =>
                r.id === editingCommentId
                  ? {
                      ...updatedComment,
                      // Don't include replies for reply comments
                      replies: undefined,
                    }
                  : r
              ),
            };
          }
          return c;
        })
      );
      setEditingCommentId(null);
      setEditText('');
      toast.success('Comment updated successfully');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update comment');
    }
  };

  const handleDeleteComment = async () => {
    try {
      await deleteTaskComment(selectedComment.id);
      // Remove comment from the list, checking both top-level and nested replies
      setComments((prev) =>
        prev
          .filter((c) => c.id !== selectedComment.id)
          .map((c) => ({
            ...c,
            replies: (c.replies || []).filter((r) => r.id !== selectedComment.id),
          }))
      );
      setMenuAnchor(null);
      setSelectedComment(null);
      toast.success('Comment deleted successfully');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete comment');
    }
  };

  const handleReplyClick = (commentId) => {
    setReplyingToCommentId(commentId);
    setReplyText('');
  };

  const handleCancelReply = () => {
    setReplyingToCommentId(null);
    setReplyText('');
  };

  const handleMenuOpen = (event, comment) => {
    setMenuAnchor(event.currentTarget);
    setSelectedComment(comment);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedComment(null);
  };

  const handleReplyFromMenu = () => {
    if (selectedComment) {
      handleReplyClick(selectedComment.id);
      handleMenuClose();
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
          Comments
        </Typography>
        <Tooltip title="Refresh comments">
          <IconButton
            size="small"
            onClick={() => fetchComments(true)}
            disabled={refreshing}
            sx={{
              color: '#6366f1',
              '&:hover': { backgroundColor: '#f3f4f6' },
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          >
            <RefreshIcon
              fontSize="small"
              sx={{
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                transition: 'transform 0.3s ease',
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Comments List */}
      <Box
        ref={commentsContainerRef}
        sx={{
          mb: 3,
          maxHeight: '400px',
          overflowY: 'auto',
          position: 'relative',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '4px',
            '&:hover': {
              background: '#a8a8a8',
            },
          },
        }}
      >
        {showScrollToBottom && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              display: 'flex',
              justifyContent: 'center',
              zIndex: 10,
              mb: 1,
            }}
          >
            <Tooltip title="Scroll to latest comments">
              <Button
                size="small"
                variant="contained"
                startIcon={<KeyboardArrowDownIcon />}
                onClick={scrollToBottom}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: 2,
                  backgroundColor: '#6366f1',
                  '&:hover': {
                    backgroundColor: '#4f46e5',
                  },
                }}
              >
                Go to Bottom
              </Button>
            </Tooltip>
          </Box>
        )}
        {loading ? (
          <Typography sx={{ color: '#6b7280', fontSize: 14 }}>Loading comments...</Typography>
        ) : comments.length === 0 ? (
          <Typography sx={{ color: '#6b7280', fontSize: 14, fontStyle: 'italic' }}>
            No comments yet. Be the first to comment!
          </Typography>
        ) : (
          comments.map((comment) => (
            <Box
              key={comment.id}
              sx={{
                mb: 2,
                pb: 2,
                borderBottom: '1px solid #e5e7eb',
                '&:last-child': {
                  borderBottom: 'none',
                },
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                {/* Avatar */}
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: '#6366f1',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {getInitials(comment?.commentAuthor?.userName)}
                </Avatar>

                {/* Comment Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>
                      {comment?.commentAuthor?.userName === accountData?.userName
                        ? 'You'
                        : comment?.commentAuthor?.userName || 'Unknown User'}
                    </Typography>
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, comment)} sx={{ p: 0.5 }}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>

                  </Box>

                  {editingCommentId === comment.id ? (
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        sx={{ mb: 1 }}
                        autoFocus
                      />
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="contained" onClick={handleUpdateComment}>
                          Save
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setEditingCommentId(null);
                            setEditText('');
                          }}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Box>
                  ) : (
                    <>
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: '#374151',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          mb: 0.5,
                        }}
                      >
                        {comment.comment}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#6b7280', mb: 1 }}>
                        {comment.createdAt === comment.updatedAt ? (
                          format(new Date(comment.createdAt), 'dd-MMM-yy hh:mm a')
                        ) : (
                          <span> edited {format(new Date(comment.updatedAt), 'dd-MMM-yy hh:mm a')}</span>
                        )}
                      </Typography>
                    </>
                  )}

                  {/* Replies Section */}
                  {comment.replies && comment.replies.length > 0 && (
                    <Box sx={{ ml: 2, mt: 2, pl: 2, borderLeft: '2px solid #e5e7eb' }}>
                      {comment.replies.map((reply) => (
                        <Box key={reply.id} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                bgcolor: '#6366f1',
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            >
                              {getInitials(reply?.commentAuthor?.userName)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box
                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}
                              >
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
                                  {reply?.commentAuthor?.userName === accountData?.userName
                                    ? 'You'
                                    : reply?.commentAuthor?.userName || 'Unknown User'}
                                </Typography>
                                {reply?.commentAuthor?.userName === accountData?.userName && (
                                  <IconButton size="small" onClick={(e) => handleMenuOpen(e, reply)} sx={{ p: 0.5 }}>
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>

                              {editingCommentId === reply.id ? (
                                <Box>
                                  <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={editText}
                                    onChange={(e) => setEditText(e.target.value)}
                                    sx={{ mb: 1 }}
                                    autoFocus
                                    size="small"
                                  />
                                  <Stack direction="row" spacing={1}>
                                    <Button size="small" variant="contained" onClick={handleUpdateComment}>
                                      Save
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => {
                                        setEditingCommentId(null);
                                        setEditText('');
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </Stack>
                                </Box>
                              ) : (
                                <>
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      color: '#374151',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      mb: 0.5,
                                    }}
                                  >
                                    {reply.comment}
                                  </Typography>
                                  <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                                    {reply.createdAt === reply.updatedAt ? (
                                      format(new Date(reply.createdAt), 'dd-MMM-yy hh:mm a')
                                    ) : (
                                      <span> edited {format(new Date(reply.updatedAt), 'dd-MMM-yy hh:mm a')}</span>
                                    )}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Reply Input */}
                  {replyingToCommentId === comment.id && (
                    <Box sx={{ mt: 2, ml: 2, pl: 2, borderLeft: '2px solid #e5e7eb' }}>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: '#6366f1',
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {getInitials(accountData?.userName)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            placeholder="Write a reply..."
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                handleAddComment(comment.id);
                              }
                            }}
                            sx={{
                              mb: 1,
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#f9fafb',
                                fontSize: 13,
                                '&:hover': {
                                  backgroundColor: '#f3f4f6',
                                },
                                '&.Mui-focused': {
                                  backgroundColor: '#ffffff',
                                },
                              },
                            }}
                            autoFocus
                          />
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<SendIcon />}
                              onClick={() => handleAddComment(comment.id)}
                              disabled={!replyText.trim() || submitting}
                              sx={{
                                textTransform: 'none',
                                fontSize: 12,
                                px: 1.5,
                              }}
                            >
                              {submitting ? 'Adding...' : 'Reply'}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CloseIcon />}
                              onClick={handleCancelReply}
                              sx={{
                                textTransform: 'none',
                                fontSize: 12,
                                px: 1.5,
                              }}
                            >
                              Cancel
                            </Button>
                          </Stack>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Box>

      {/* Add Comment Input */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: '#6366f1',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {getInitials(accountData?.userName)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            placeholder="Write a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                handleAddComment();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f9fafb',
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                },
                '&.Mui-focused': {
                  backgroundColor: '#ffffff',
                },
              },
            }}
            inputRef={commentInputRef}
          />
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<SendIcon />}
              onClick={() => handleAddComment()}
              disabled={!newComment.trim() || submitting}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                px: 2,
              }}
            >
              {submitting ? 'Adding...' : 'Comment'}
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Menu for Reply/Edit/Delete */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* Only show Reply option for top-level comments (not replies) */}
        {selectedComment && !selectedComment.parentCommentId && (
          <MenuItem onClick={handleReplyFromMenu}>
            <ReplyIcon sx={{ mr: 1, fontSize: 18 }} />
            Reply
          </MenuItem>
        )}
        {selectedComment && accountData?.id === selectedComment?.createdBy && (
          <>
            {selectedComment && !selectedComment.parentCommentId && <Divider />}
            <MenuItem
              onClick={() => {
                handleEditComment(selectedComment);
              }}
            >
              <EditIcon sx={{ mr: 1, fontSize: 18 }} />
              Edit
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleDeleteComment} sx={{ color: 'error.main' }}>
              <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
              Delete
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default TaskComments;
